/**
 * The publish state of a course or a program, shown and changed where you
 * configure it.
 *
 * Publishing used to be reachable only from the detail page, while the
 * configure wizard's "Save as draft" referred to the invitation rather than to
 * the thing itself — so a published program stayed published no matter which
 * button you pressed there (Aleix, 2026-09-11). One control, used by both
 * wizards, so the two surfaces cannot drift apart again.
 */
import React from 'react';
import { cn } from '../../lib/utils';

export type PublishableStatus = 'draft' | 'published' | 'active' | 'archived' | 'disabled';

interface Props {
  /** What is being published — drives the wording only. */
  entity: 'course' | 'program';
  status: PublishableStatus;
  pending?: boolean;
  onPublish: () => void;
  onUnpublish: () => void;
  className?: string;
}

const LIVE: PublishableStatus[] = ['published', 'active'];

const PublishStatusControl: React.FC<Props> = ({
  entity,
  status,
  pending = false,
  onPublish,
  onUnpublish,
  className,
}) => {
  const isLive = LIVE.includes(status);
  const isArchived = status === 'archived' || status === 'disabled';

  return (
    <div
      className={cn(
        'inline-flex items-center gap-3 rounded-full border border-border-primary/30 py-1 pl-4 pr-1',
        className
      )}
      data-testid={`${entity}-publish-control`}
    >
      <span className="text-xs font-semibold text-text-secondary">
        {isArchived
          ? 'Hidden'
          : isLive
            ? `Published — learners can see this ${entity}`
            : `Draft — not visible to learners`}
      </span>

      {!isArchived && (
        <button
          type="button"
          onClick={isLive ? onUnpublish : onPublish}
          disabled={pending}
          data-testid={`${entity}-publish-toggle`}
          className={cn(
            'inline-flex items-center rounded-full px-3.5 py-1.5 text-xs font-bold',
            'transition-[background-color,transform] duration-150 focus-ring',
            'active:scale-[0.97] disabled:opacity-60 disabled:cursor-not-allowed',
            isLive
              ? 'border border-border-primary/40 text-text-primary hover:bg-text-primary/[0.06]'
              : 'bg-primary-500 hover:bg-primary-600 dark:hover:bg-primary-400'
          )}
        >
          {pending ? 'Saving…' : isLive ? 'Unpublish' : 'Publish'}
        </button>
      )}
    </div>
  );
};

export default PublishStatusControl;
